import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Shield, Search, Download, Loader2, User, Clock, ArrowRight,
  FileText, Package, Building2, Server, Users, Settings,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const ACTION_META = {
  bulk_tag: { icon: Package, color: 'text-emerald-600' },
  comment_added: { icon: FileText, color: 'text-blue-600' },
  snapshot_created: { icon: FileText, color: 'text-indigo-600' },
  property_created: { icon: Building2, color: 'text-emerald-600' },
  property_updated: { icon: Building2, color: 'text-blue-600' },
  property_deleted: { icon: Building2, color: 'text-rose-600' },
  user_created: { icon: Users, color: 'text-emerald-600' },
  user_deleted: { icon: Users, color: 'text-rose-600' },
  role_changed: { icon: Shield, color: 'text-amber-600' },
  tenant_created: { icon: Settings, color: 'text-emerald-600' },
  tenant_deleted: { icon: Settings, color: 'text-rose-600' },
};

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ''; }
}

/**
 * AuditLogViewer — filterable, exportable timeline of admin actions. Sits in the
 * Admin panel and consumes the existing `/api/admin/audit-log` endpoint.
 */
export default function AuditLogViewer({ token }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (entityFilter !== 'all') params.entity = entityFilter;
      if (actionFilter !== 'all') params.action = actionFilter;
      const r = await axios.get(`${BACKEND}/api/admin/audit-log`, { headers, params });
      setEntries(r.data.entries || []);
    } catch { setEntries([]); }
    finally { setLoading(false); }
  }, [entityFilter, actionFilter, headers]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return entries;
    const ql = q.toLowerCase();
    return entries.filter(e =>
      (e.user_email || '').toLowerCase().includes(ql) ||
      (e.action || '').toLowerCase().includes(ql) ||
      (e.entity || '').toLowerCase().includes(ql) ||
      (e.entity_id || '').toLowerCase().includes(ql) ||
      (e.details || '').toLowerCase().includes(ql)
    );
  }, [entries, q]);

  // Group by day
  const grouped = useMemo(() => {
    const byDay = new Map();
    filtered.forEach(e => {
      let day = 'Unknown';
      try {
        const d = new Date(e.timestamp);
        day = d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
      } catch { /* empty */ }
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push(e);
    });
    return [...byDay.entries()];
  }, [filtered]);

  const uniqueEntities = useMemo(() => {
    const s = new Set(entries.map(e => e.entity).filter(Boolean));
    return [...s].sort();
  }, [entries]);
  const uniqueActions = useMemo(() => {
    const s = new Set(entries.map(e => e.action).filter(Boolean));
    return [...s].sort();
  }, [entries]);

  const exportCsv = () => {
    const rows = [['Timestamp', 'User', 'Action', 'Entity', 'Entity ID', 'Details']];
    filtered.forEach(e => rows.push([
      e.timestamp || '', e.user_email || '', e.action || '', e.entity || '', e.entity_id || '', (e.details || '').replace(/\n/g, ' '),
    ]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="audit-log-viewer">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Shield className="h-5 w-5 text-indigo-500" /> Audit Log
          </h1>
          <p className="text-xs text-muted-foreground mt-1">Timeline of all admin actions across your tenant. Filterable and exportable for SOC2 / ISO reviewers.</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={filtered.length === 0} data-testid="audit-export-csv">
          <Download className="h-3.5 w-3.5 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap p-3 rounded-lg border bg-card/50">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            className="pl-9 h-8 text-sm"
            placeholder="Filter by user, action, entity, or details..."
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="audit-search"
          />
        </div>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="audit-entity-filter">
            <SelectValue placeholder="Entity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            {uniqueEntities.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="audit-action-filter">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {uniqueActions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-[10px] text-muted-foreground ml-auto">
          {loading ? 'Loading…' : `${filtered.length} of ${entries.length} events`}
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="py-16 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground border rounded-xl bg-muted/20">
          <Shield className="h-8 w-8 mx-auto mb-3 opacity-30" />
          No events match the current filters.
        </div>
      ) : (
        <div className="space-y-6" data-testid="audit-timeline">
          {grouped.map(([day, evs]) => (
            <div key={day}>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                <Clock className="h-3 w-3" /> {day} <Badge variant="outline" className="text-[9px]">{evs.length}</Badge>
              </div>
              <div className="relative border-l-2 border-border ml-2 space-y-1 pb-2">
                {evs.map(e => {
                  const meta = ACTION_META[e.action] || { icon: Server, color: 'text-slate-500' };
                  const Icon = meta.icon;
                  return (
                    <div key={e.id} className="pl-6 relative group" data-testid={`audit-event-${e.id}`}>
                      <div className={`absolute -left-[9px] top-1.5 h-4 w-4 rounded-full bg-background border-2 border-current ${meta.color} flex items-center justify-center`}>
                        <Icon className="h-2 w-2" />
                      </div>
                      <div className="py-1.5 px-3 rounded-md hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap text-xs">
                              <User className="h-3 w-3 text-muted-foreground shrink-0" />
                              <span className="font-semibold">{e.user_email || 'system'}</span>
                              <span className="text-muted-foreground">·</span>
                              <Badge variant="outline" className={`text-[9px] ${meta.color}`}>{e.action}</Badge>
                              <ArrowRight className="h-3 w-3 text-muted-foreground" />
                              <span className="font-medium text-muted-foreground">{e.entity}</span>
                              {e.entity_id && <span className="text-[10px] font-mono text-muted-foreground truncate">#{e.entity_id.slice(0, 12)}</span>}
                            </div>
                            {e.details && <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{e.details}</div>}
                          </div>
                          <span className="text-[10px] text-muted-foreground shrink-0" title={e.timestamp}>{timeAgo(e.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
